import {
  Close,
  EIconButtonVariant,
  ETextStyleVariant,
  GeneralMapLayersIcon,
  IconButton,
  Overlay,
  Sheet,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMenuTriggerState } from 'react-stately';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useReMountableElementRef } from '@/hooks/useReMountableElementRef';
import { MapboxStyle } from '@/services/types/map';

import { MapLayerButtons } from './MapLayerButtons';

interface IProps {
  isDesktopMapViewExpanded?: boolean;
  activeStyle: MapboxStyle;
  setMapboxStyle: (styleType: MapboxStyle) => void;
}

export const MapLayers: React.FC<IProps> = ({
  activeStyle,
  isDesktopMapViewExpanded,
  setMapboxStyle,
}) => {
  const intl = useIntl();
  const { isAboveXL } = useBreakpoint();
  const [isMapLayersMenuOpen, setIsMapStyleMenuOpen] = useState(false);
  const { ref: triggerRef, setElement: setFieldElement } = useReMountableElementRef<HTMLElement>();
  const triggerState = useMenuTriggerState({
    isOpen: isMapLayersMenuOpen,
    onOpenChange: open => {
      setIsMapStyleMenuOpen(open);
    },
  });
  const isMenuOpened = triggerState.isOpen;

  const handleMapButtonClick = () => {
    if (isMenuOpened) {
      triggerState.close();
    } else {
      triggerState.open();
    }
  };

  const closeLayersMenu = () => {
    triggerState.close();
  };

  if (isAboveXL) {
    return (
      <>
        <IconButton
          className="mr-3 !text-750"
          ref={setFieldElement}
          icon={GeneralMapLayersIcon}
          onClick={handleMapButtonClick}
          size="small"
          variant={EIconButtonVariant.Primary}
        />
        {isMenuOpened && (
          <Overlay
            triggerRef={triggerRef}
            triggerState={triggerState}
            popperProps={{
              placement: 'bottom',
            }}
            className={clsx('z-[1310] !top-4', {
              '!left-24': !isDesktopMapViewExpanded,
              '!left-4': isDesktopMapViewExpanded,
            })}
            underlay
            underlayClassname="z-[1299]">
            <div className="p-6 bg-primary-contrast rounded-xl">
              <div className="flex flex-row items-center justify-between mb-6">
                <Text variant={ETextStyleVariant.LargeBold}>
                  {intl.formatMessage({ defaultMessage: 'Base map', id: 'VSYPxa' })}
                </Text>
                <Close onClose={closeLayersMenu} />
              </div>
              <MapLayerButtons activeStyle={activeStyle} setMapboxStyle={setMapboxStyle} />
            </div>
          </Overlay>
        )}
      </>
    );
  }

  return (
    <>
      <IconButton
        className="mr-3 !text-750"
        ref={setFieldElement}
        icon={GeneralMapLayersIcon}
        onClick={handleMapButtonClick}
        size="small"
        variant={EIconButtonVariant.Primary}
      />
      {isMenuOpened && (
        <Sheet
          isOpen={isMenuOpened}
          onClose={closeLayersMenu}
          ariaDialogLabel={intl.formatMessage({ defaultMessage: 'Select map layer', id: 'yq8YM9' })}
          isDismissable={true}>
          <Sheet.Content className="mt-6">
            <div className="bg-primary-contrast rounded-xl">
              <div className="flex flex-row items-center justify-between mb-6">
                <Text variant={ETextStyleVariant.LargeBold}>
                  {intl.formatMessage({ defaultMessage: 'Base map', id: 'VSYPxa' })}
                </Text>
                <Close onClose={closeLayersMenu} />
              </div>
              <MapLayerButtons activeStyle={activeStyle} setMapboxStyle={setMapboxStyle} />
            </div>
          </Sheet.Content>
        </Sheet>
      )}
    </>
  );
};
